import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  message, 
  Modal, 
  Tag, 
  Statistic, 
  Row, 
  Col, 
  Tooltip,
  Descriptions,
  Upload,
  Breadcrumb
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  DollarCircleOutlined,
  FileTextOutlined,
  DownloadOutlined,
  UploadOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  RollbackOutlined
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doctorService } from '../services/doctorService';
import axios from '../utils/axios';
import dayjs from 'dayjs';

// 安全的金额格式化函数
const formatAmount = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) {
    return '0.00';
  }
  return amount.toFixed(2);
};

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

// 接口定义
interface LaborDetail {
  id: number;
  doctor_id: number;
  project_id: number;
  volunteer_id?: number;
  survey_count: number;
  submission_ids: string;
  original_amount: number;
  after_tax_amount: number;
  before_tax_amount: number;
  tax_amount: number;
  account_period?: string;
  settled_at?: string;
  settled_by?: number;
  paid_at?: string;
  paid_by?: number;
  remark?: string;
  createdAt: string;
  updatedAt: string;
  
  // 多级审批字段
  volunteer_status: 'pending' | 'confirmed' | 'rejected';
  volunteer_confirmed_at?: string;
  volunteer_confirmed_count?: number;
  volunteer_confirmed_amount?: number;
  volunteer_remark?: string;
  
  responsible_status: 'pending' | 'confirmed' | 'rejected';
  responsible_confirmed_at?: string;
  responsible_reject_reason?: string;
  
  overall_status: 'volunteer_pending' | 'responsible_pending' | 'admin_pending' | 'admin_settled' | 'paid' | 'rejected';
  project_type: 'survey' | 'ambassador';
  doctor?: {
    id: number;
    name: string;
    departmentName?: string;
    phone?: string;
    hospitalName?: string;
    title?: string;
    idCard?: string;
    volunteers?: Array<{
        id: number;
        name: string;
        phone?: string;
        levels?: string | string[] | Array<{name: string, value: string, order: number}>;
        DoctorVolunteer?: {
          doctor_code?: string;
          hospital_code?: string;
        };
      }>;
  };
  project?: {
    id: number;
    name: string;
    description?: string;
  };
  settler?: {
    id: number;
    name: string;
  };
  payer?: {
    id: number;
    name: string;
  };
  agreementDocument?: {
    id: number;
    status: 'generated' | 'failed';
    file_url?: string;
    file_name?: string;
    error_message?: string;
    generated_at?: string;
  };
}



// 新的确认状态统计
interface ConfirmationStats {
  volunteer_pending: number;   // 志愿者待确认
  responsible_pending: number; // 负责人待确认
  admin_pending: number;       // 管理员待审核
  admin_settled: number;       // 已审核待支付
  paid: number;                // 已支付
  total_amount: number;        // 总金额
  confirmed_amount: number;    // 已确认金额
}

const LaborDetailManagement: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const projectName = location.state?.projectName || '';
  const projectType = location.state?.projectType || '';
  
  const [data, setData] = useState<LaborDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchForm] = Form.useForm();
  const [confirmationStats, setConfirmationStats] = useState<ConfirmationStats | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState<LaborDetail | null>(null);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [currentProject, setCurrentProject] = useState<any>(null);
  const [oaImportStatus, setOaImportStatus] = useState<any>(null);
  const [agreementGenerating, setAgreementGenerating] = useState(false);
  const [importCodeGenerating, setImportCodeGenerating] = useState(false);
  const [importCodeModalVisible, setImportCodeModalVisible] = useState(false);
  const [generatedImportCode, setGeneratedImportCode] = useState<any>(null);
  
  // 新增：医生搜索相关状态
  const [doctorSearchLoading, setDoctorSearchLoading] = useState(false);
  const searchTimeoutRef = React.useRef<any>(null);




  // 整体状态颜色映射
  const getOverallStatusColor = (status: string) => {
    switch (status) {
      case 'volunteer_pending': return 'orange';
      case 'responsible_pending': return 'blue';
      case 'admin_pending': return 'cyan';
      case 'admin_settled': return 'green';
      case 'paid': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  // 整体状态文本映射
  const getOverallStatusText = (status: string) => {
    switch (status) {
      case 'volunteer_pending': return '志愿者待确认';
      case 'responsible_pending': return '负责人待确认';
      case 'admin_pending': return '管理员待审核';
      case 'admin_settled': return '已审核待支付';
      case 'paid': return '已支付';
      case 'rejected': return '已拒绝';
      default: return '未知';
    }
  };

  // 志愿者状态颜色映射
  const getVolunteerStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'confirmed': return 'green';
      case 'rejected': return 'red';
      default: return 'default';
    }
  };

  // 志愿者状态文本映射
  const getVolunteerStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '待确认';
      case 'confirmed': return '已确认';
      case 'rejected': return '已拒绝';
      default: return '未知';
    }
  };

  // 负责人状态颜色映射
  const getResponsibleStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'confirmed': return 'green';
      case 'rejected': return 'red';
      default: return 'default';
    }
  };

  // 负责人状态文本映射
  const getResponsibleStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '待确认';
      case 'confirmed': return '已确认';
      case 'rejected': return '已拒绝';
      default: return '未知';
    }
  };

  // 获取当前项目信息
  const fetchCurrentProject = async () => {
    if (!projectId) return;
    
    try {
      const response = await axios.get(`/projects/${projectId}`);
      if (response.data.success) {
        setCurrentProject(response.data.data);
      }
    } catch (error) {
      console.error('获取项目信息失败:', error);
    }
  };

  // 获取劳务明细列表
  const fetchData = async (page = 1, size = 10, filters?: any) => {
    try {
      setLoading(true);
      const params: any = {
        page,
        pageSize: size,
        project_id: projectId, // 自动添加项目ID过滤
        ...filters
      };

      const response = await axios.get('/labor-details', { params });
      
      if (response.data.success) {
        setData(response.data.data.list);
        setTotal(response.data.data.total);
        setCurrentPage(page);
        setPageSize(size);
      } else {
        message.error('获取劳务明细失败');
      }
    } catch (error) {
      console.error('获取劳务明细失败:', error);
      message.error('获取劳务明细失败');
    } finally {
      setLoading(false);
    }
  };


  // 获取确认状态统计
  const fetchConfirmationStats = async () => {
    try {
      const response = await axios.get('/labor-details/statistics', {
        params: {
          project_id: projectId // 传递当前项目ID
        }
      });
      
      if (response.data.success) {
        setConfirmationStats(response.data.data);
      }
    } catch (error) {
      console.error('获取确认状态统计失败:', error);
    }
  };

  // 获取OA导入准备状态
  const fetchOaImportStatus = async () => {
    if (!projectId) return;
    try {
      const response = await axios.get(`/labor-details/project/${projectId}/oa-import-status`);
      if (response.data.success) {
        setOaImportStatus(response.data.data);
      }
    } catch (error) {
      console.error('获取OA导入状态失败:', error);
    }
  };

  // 一键生成当前项目已审核待支付记录的劳务协议
  const generateAllLaborAgreements = async () => {
    if (!projectId) return;
    try {
      setAgreementGenerating(true);
      const response = await axios.post(`/labor-details/project/${projectId}/generate-agreements`);
      if (response.data.success) {
        const data = response.data.data || {};
        if (data.failed > 0) {
          Modal.warning({
            title: '劳务协议生成完成，但存在失败记录',
            width: 720,
            content: (
              <div>
                <p>{response.data.message}</p>
                <div style={{ maxHeight: 260, overflow: 'auto', background: '#fff7e6', padding: 12, borderRadius: 4 }}>
                  {(data.errors || []).slice(0, 20).map((item: string, index: number) => (
                    <div key={index} style={{ marginBottom: 6 }}>{item}</div>
                  ))}
                </div>
              </div>
            )
          });
        } else {
          message.success(response.data.message || '劳务协议生成完成');
        }
        fetchData(currentPage, pageSize);
        fetchOaImportStatus();
      } else {
        message.error(response.data.message || '生成劳务协议失败');
      }
    } catch (error: any) {
      console.error('生成劳务协议失败:', error);
      message.error(error.response?.data?.message || '生成劳务协议失败');
    } finally {
      setAgreementGenerating(false);
    }
  };

  // 所有协议生成后生成OA导入码
  const generateOaImportCode = async () => {
    if (!projectId) return;
    try {
      setImportCodeGenerating(true);
      const response = await axios.post(`/labor-details/project/${projectId}/import-code`);
      if (response.data.success) {
        setGeneratedImportCode(response.data.data);
        setImportCodeModalVisible(true);
        fetchOaImportStatus();
      } else {
        message.error(response.data.message || '生成OA导入码失败');
      }
    } catch (error: any) {
      console.error('生成OA导入码失败:', error);
      message.error(error.response?.data?.message || '生成OA导入码失败');
    } finally {
      setImportCodeGenerating(false);
    }
  };


  // 搜索医生列表 (替换原有的 fetchDoctors)
  const searchDoctors = async (keyword: string) => {
    try {
      setDoctorSearchLoading(true);
      const params: any = {
        page: 1,
        pageSize: 50
      };
      if (keyword) {
        params.name = keyword;
      }

      const response = await doctorService.getDoctors(params);
      if (response.success) {
        setDoctors(response.data);
      }
    } catch (error) {
      console.error('搜索医生失败:', error);
    } finally {
      setDoctorSearchLoading(false);
    }
  };

  const handleDoctorSearch = (value: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchDoctors(value);
    }, 800);
  };

  // 生成劳务明细
  const generateLaborDetails = async () => {
    try {
      // 处理带参数的生成，必须选择时间范围
      const values = searchForm.getFieldsValue();
      
      // 验证必填字段
      if (!values.dateRange || values.dateRange.length !== 2) {
        message.error('请选择时间范围');
        return;
      }
      
      const filters: any = {
        project_id: projectId, // 自动添加当前项目ID
        start_date: values.dateRange[0].format('YYYY-MM-DD'),
        end_date: values.dateRange[1].format('YYYY-MM-DD')
      };

      setLoading(true);
      const response = await axios.post('/labor-details/generate', filters);
      
      if (response.data.success) {
        message.success(response.data.message);
        fetchData(currentPage, pageSize);
        fetchConfirmationStats();
        fetchOaImportStatus();
      } else {
        message.error(response.data.message || '生成劳务明细失败');
      }
    } catch (error: any) {
      console.error('生成劳务明细失败:', error);
      message.error(error.response?.data?.message || '生成劳务明细失败');
    } finally {
      setLoading(false);
    }
  };

  // 批量结算
  const batchSettle = async () => {
    let pendingRecords: LaborDetail[] = [];
    let isSelectAll = false;
    let allPendingCount = 0;

    if (selectedRowKeys.length === 0) {
      // 如果没有选择任何记录，先获取所有符合条件的记录数量
      try {
        const response = await axios.get('/labor-details', {
          params: {
            overall_status: 'admin_pending', // 只获取管理员待审核的记录
            pageSize: 1000000, // 获取所有记录
            project_id: projectId
          }
        });
        
        if (response.data.success) {
          pendingRecords = response.data.data.list || [];
          allPendingCount = response.data.data.total || 0;
          isSelectAll = true;
        }
      } catch (error) {
        console.error('获取待审核记录失败:', error);
        message.error('获取待审核记录失败');
        return;
      }
    } else {
      // 如果有选择记录，按原逻辑处理
      const selectedRecords = data.filter(record => selectedRowKeys.includes(record.id));
      pendingRecords = selectedRecords.filter(record => 
        record.overall_status === 'admin_pending'
      );
    }
    
    if (pendingRecords.length === 0) {
      message.warning('没有可审核的记录，请确保记录状态为"已确认"且"待审核"');
      return;
    }

    const totalAmount = pendingRecords.reduce((sum: number, record: any) => sum + record.after_tax_amount, 0);
    const selectedRecords = selectedRowKeys.length > 0 ? data.filter(record => selectedRowKeys.includes(record.id)) : [];

    let remark = '';
    Modal.confirm({
      title: '批量审核',
      width: 600,
      content: (
        <div>
          <p>
            {isSelectAll 
              ? `确定要审核全部符合条件的 ${pendingRecords.length} 条记录吗？` 
              : `确定要审核选中的 ${pendingRecords.length} 条记录吗？`
            }
          </p>
          <div style={{ backgroundColor: '#f5f5f5', padding: '12px', margin: '12px 0', borderRadius: '4px' }}>
            <p><strong>审核详情：</strong></p>
            <p>• 审核范围：{isSelectAll ? '全部符合条件的记录' : '选中的记录'}</p>
            <p>• 审核记录数：{pendingRecords.length} 条</p>
                            <p>• 总金额：¥{formatAmount(totalAmount)}</p>
          </div>
          {!isSelectAll && selectedRecords.length > pendingRecords.length && (
            <p style={{ color: '#faad14' }}>
              注意：已自动过滤了 {selectedRecords.length - pendingRecords.length} 条不符合条件的记录
            </p>
          )}
          {isSelectAll && (
            <p style={{ color: '#1890ff' }}>
              提示：您没有选择具体记录，系统将审核全部"已确认"且"待审核"的记录
            </p>
          )}
          <TextArea
            placeholder="审核备注（可选）"
            onChange={(e) => remark = e.target.value}
            rows={3}
          />
        </div>
      ),
      onOk: async () => {
        try {
          let response;
          if (isSelectAll) {
            // 如果是审核全部，使用特殊的API或传递特殊标识
            response = await axios.post('/labor-details/batch-settle', {
              selectAll: true, // 标识要审核全部符合条件的记录
              projectId,
              remark
            });
          } else {
            // 如果是审核选中的，传递具体的ID列表
            response = await axios.post('/labor-details/batch-settle', {
              ids: pendingRecords.map((record: any) => record.id),
              remark
            });
          }
          
          if (response.data.success) {
            message.success(`审核成功！已审核 ${response.data.data?.count || pendingRecords.length} 条记录`);
            setSelectedRowKeys([]);
            fetchData(currentPage, pageSize);
            fetchConfirmationStats();
            fetchOaImportStatus();
          } else {
            message.error(response.data.message || '审核失败');
          }
        } catch (error: any) {
          console.error('批量审核失败:', error);
          message.error(error.response?.data?.message || '批量审核失败');
        }
      }
    });
  };

  // 导出支付确认Excel
  const exportPaymentExcel = async () => {
    try {
      const filters = searchForm.getFieldsValue();
      const params: any = {
        status: 'settled', // 只导出已审核待支付的记录
        ...filters
      };
      
      params.project_id = projectId;
      if (filters.doctor_id) params.doctor_id = filters.doctor_id;
      if (filters.overall_status) params.overall_status = filters.overall_status;
      if (filters.dateRange && filters.dateRange.length === 2) {
        params.start_date = filters.dateRange[0].format('YYYY-MM-DD');
        params.end_date = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await axios.get('/labor-details/export/payment-template', {
        params,
        responseType: 'blob'
      });

      // 检查响应是否是错误（通过content-type判断）
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        // 如果是JSON响应，说明是错误信息
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        message.error(errorData.message || '导出失败');
        return;
      }

      // 创建下载链接
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `支付确认表_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success('支付确认表导出成功，请在Excel中添加"支付结果"列并填写"成功"后重新导入');
    } catch (error: any) {
      console.error('导出支付确认表失败:', error);
      
      // 处理blob响应中的错误信息
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          message.error(errorData.message || '导出支付确认表失败');
        } catch {
          message.error('导出支付确认表失败');
        }
      } else {
        message.error(error.response?.data?.message || '导出支付确认表失败');
      }
    }
  };

  // 导入支付结果
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // 删除相关状态
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 退回相关状态
  const [resetLoading, setResetLoading] = useState(false);

  // 退回单个劳务明细至志愿者待确认状态
  const resetToPending = async (record: LaborDetail) => {
    const allowedStatuses = ['responsible_pending', 'admin_pending'];
    if (!allowedStatuses.includes(record.overall_status)) {
      message.warning('只能退回"负责人待确认"或"管理员待审核"状态的劳务明细');
      return;
    }

    const statusText = record.overall_status === 'responsible_pending' ? '负责人待确认' : '管理员待审核';

    Modal.confirm({
      title: '确认退回',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>确定要退回这条劳务明细吗？</p>
          <div style={{ backgroundColor: '#fff7e6', padding: '12px', margin: '12px 0', borderRadius: '4px', border: '1px solid #ffd591' }}>
            <p style={{ margin: '4px 0' }}><strong>医生：</strong>{record.doctor?.name || '未知'}</p>
            <p style={{ margin: '4px 0' }}><strong>项目：</strong>{record.project?.name || '未知'}</p>
            <p style={{ margin: '4px 0' }}><strong>当前状态：</strong>{statusText}</p>
            <p style={{ margin: '4px 0' }}><strong>原始税后金额：</strong>¥{formatAmount(record.original_amount)}</p>
          </div>
          <p style={{ color: '#fa8c16', margin: 0 }}>
            退回后将清除已有的确认记录，金额恢复为原始值，等待志愿者重新确认。
          </p>
        </div>
      ),
      okText: '确认退回',
      okButtonProps: { style: { backgroundColor: '#fa8c16', borderColor: '#fa8c16' } },
      cancelText: '取消',
      onOk: async () => {
        try {
          setResetLoading(true);
          const response = await axios.put(`/labor-details/${record.id}/reset`);
          if (response.data.success) {
            message.success(response.data.message);
            fetchData(currentPage, pageSize);
            fetchConfirmationStats();
            fetchOaImportStatus();
          } else {
            message.error(response.data.message || '退回失败');
          }
        } catch (error: any) {
          console.error('退回劳务明细失败:', error);
          message.error(error.response?.data?.message || '退回失败');
        } finally {
          setResetLoading(false);
        }
      }
    });
  };

  // 删除单个劳务明细
  const deleteLaborDetail = async (record: LaborDetail) => {
    if (record.overall_status !== 'volunteer_pending') {
      message.warning('只能删除志愿者待确认状态的劳务明细');
      return;
    }

    Modal.confirm({
      title: '确认删除',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>确定要删除这条劳务明细吗？</p>
          <div style={{ backgroundColor: '#f5f5f5', padding: '12px', margin: '12px 0', borderRadius: '4px' }}>
            <p><strong>医生：</strong>{record.doctor?.name || '未知'}</p>
            <p><strong>项目：</strong>{record.project?.name || '未知'}</p>
            <p><strong>问卷数量：</strong>{record.survey_count} 份</p>
            <p><strong>税后金额：</strong>¥{formatAmount(record.after_tax_amount)}</p>
          </div>
          <p style={{ color: '#ff4d4f' }}>
            注意：删除后相关的问卷提交记录将还原为审批通过状态，可重新生成劳务明细
          </p>
        </div>
      ),
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          setDeleteLoading(true);
          const response = await axios.delete(`/labor-details/${record.id}`);
          
          if (response.data.success) {
            message.success(response.data.message);
            fetchData(currentPage, pageSize);
            fetchConfirmationStats();
            fetchOaImportStatus();
          } else {
            message.error(response.data.message || '删除失败');
          }
        } catch (error: any) {
          console.error('删除劳务明细失败:', error);
          message.error(error.response?.data?.message || '删除失败');
        } finally {
          setDeleteLoading(false);
        }
      }
    });
  };

  // 批量删除劳务明细
  const batchDeleteLaborDetails = async () => {
    let pendingRecords: LaborDetail[] = [];
    let isSelectAll = false;
    let allPendingCount = 0;

    if (selectedRowKeys.length === 0) {
      // 如果没有选择任何记录，先获取所有符合条件的记录数量
      try {
        const response = await axios.get('/labor-details', {
          params: {
            project_id: projectId,
            overall_status: 'volunteer_pending', // 只获取志愿者待确认的记录
            pageSize: 1000000 // 获取所有记录
          }
        });
        
        if (response.data.success) {
          pendingRecords = response.data.data.list || [];
          allPendingCount = response.data.data.total || 0;
          isSelectAll = true;
        }
      } catch (error) {
        console.error('获取待删除记录失败:', error);
        message.error('获取待删除记录失败');
        return;
      }
    } else {
      // 如果有选择记录，按原逻辑处理
      const selectedRecords = data.filter(record => selectedRowKeys.includes(record.id));
      pendingRecords = selectedRecords.filter(record => 
        record.overall_status === 'volunteer_pending'
      );
    }
    
    if (pendingRecords.length === 0) {
      message.warning('没有可删除的记录，只能删除志愿者待确认状态的记录');
      return;
    }

    const totalAmount = pendingRecords.reduce((sum: number, record: any) => sum + record.after_tax_amount, 0);
    const selectedRecords = selectedRowKeys.length > 0 ? data.filter(record => selectedRowKeys.includes(record.id)) : [];

    Modal.confirm({
      title: '批量删除确认',
      icon: <ExclamationCircleOutlined />,
      width: 600,
      content: (
        <div>
          <p>
            {isSelectAll 
              ? `确定要删除全部符合条件的 ${pendingRecords.length} 条记录吗？` 
              : `确定要删除选中的 ${pendingRecords.length} 条记录吗？`
            }
          </p>
          <div style={{ backgroundColor: '#f5f5f5', padding: '12px', margin: '12px 0', borderRadius: '4px' }}>
            <p><strong>删除详情：</strong></p>
            <p>• 删除范围：{isSelectAll ? '全部志愿者待确认记录' : '选中的记录'}</p>
            <p>• 删除记录数：{pendingRecords.length} 条</p>
            <p>• 涉及金额：¥{formatAmount(totalAmount)}</p>
          </div>
          {!isSelectAll && selectedRecords.length > pendingRecords.length && (
            <p style={{ color: '#faad14' }}>
              注意：已自动过滤了 {selectedRecords.length - pendingRecords.length} 条不符合条件的记录
            </p>
          )}
          {isSelectAll && (
            <p style={{ color: '#1890ff' }}>
              提示：您没有选择具体记录，系统将删除全部志愿者待确认的记录
            </p>
          )}
          <p style={{ color: '#ff4d4f' }}>
            注意：删除后相关的问卷提交记录将还原为审批通过状态，可重新生成劳务明细
          </p>
        </div>
      ),
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          setDeleteLoading(true);
          let response;
          if (isSelectAll) {
            // 如果是删除全部，使用特殊的API或传递特殊标识
            response = await axios.post('/labor-details/batch-delete', {
              selectAll: true, // 标识要删除全部符合条件的记录
              projectId
            });
          } else {
            // 如果是删除选中的，传递具体的ID列表
            response = await axios.post('/labor-details/batch-delete', {
              ids: pendingRecords.map((record: any) => record.id),
              projectId
            });
          }
          
          if (response.data.success) {
            message.success(`删除成功！已删除 ${response.data.data?.count || pendingRecords.length} 条记录`);
            setSelectedRowKeys([]);
            fetchData(currentPage, pageSize);
            fetchConfirmationStats();
            fetchOaImportStatus();
          } else {
            message.error(response.data.message || '删除失败');
          }
        } catch (error: any) {
          console.error('批量删除失败:', error);
          message.error(error.response?.data?.message || '批量删除失败');
        } finally {
          setDeleteLoading(false);
        }
      }
    });
  };

  const handleImportPaymentResult = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      const response = await axios.post('/labor-details/import/payment-result', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        message.success(`支付结果导入成功：${response.data.message}`);
        setImportModalVisible(false);
        fetchData(currentPage, pageSize);
        fetchConfirmationStats();
        fetchOaImportStatus();
      } else {
        message.error(response.data.message || '导入失败');
      }
    } catch (error: any) {
      console.error('导入支付结果失败:', error);
      message.error(error.response?.data?.message || '导入支付结果失败');
    } finally {
      setUploading(false);
    }
  };


  // 查看详情
  const viewDetail = async (record: LaborDetail) => {
    try {
      const response = await axios.get(`/labor-details/${record.id}`);
      if (response.data.success) {
        setSelectedDetail(response.data.data);
        setDetailModalVisible(true);
      }
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  // 搜索处理
  const handleSearch = () => {
    const values = searchForm.getFieldsValue();
    const filters: any = {};
    
    if (values.status) filters.status = values.status;
          if (values.overall_status) filters.overall_status = values.overall_status;
    if (values.quick_filter) filters.quick_filter = values.quick_filter;
    if (values.project_id) filters.project_id = values.project_id;
    if (values.doctor_id) filters.doctor_id = values.doctor_id;
    if (values.min_amount) filters.min_amount = values.min_amount;
    if (values.max_amount) filters.max_amount = values.max_amount;
    if (values.dateRange && values.dateRange.length === 2) {
      filters.start_date = values.dateRange[0].format('YYYY-MM-DD');
      filters.end_date = values.dateRange[1].format('YYYY-MM-DD');
    }
    
    fetchData(1, pageSize, filters);
    fetchConfirmationStats(); // 刷新确认状态统计
    fetchOaImportStatus();
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    fetchData(1, pageSize);
    fetchConfirmationStats();
    fetchOaImportStatus();
  };



  useEffect(() => {
    if (projectId) {
      fetchCurrentProject();
      fetchData();
      fetchConfirmationStats();
      fetchOaImportStatus();
    }
  }, [projectId]);

  const columns: ColumnsType<LaborDetail> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: '医生信息',
      key: 'doctor',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.doctor?.name || '-'}</div>
          {record.doctor?.departmentName && (
            <div style={{ fontSize: 12, color: '#666' }}>{record.doctor.departmentName}</div>
          )}
          {record.doctor?.phone && (
            <div style={{ fontSize: 12, color: '#666' }}>{record.doctor.phone}</div>
          )}
          {record.doctor?.hospitalName && (
            <div style={{ fontSize: 12, color: '#999' }}>{record.doctor.hospitalName}</div>
          )}
        </div>
      ),
    },
    {
      title: '志愿者信息',
      key: 'volunteer',
      width: 160,
      align: 'center',
      render: (_, record) => {
        const volunteer = record.doctor?.volunteers?.[0];
        if (!volunteer) return '-';
        
        const level = volunteer.levels 
          ? (() => {
              if (Array.isArray(volunteer.levels) && volunteer.levels.length > 0 && typeof volunteer.levels[0] === 'object' && volunteer.levels[0] !== null && 'name' in volunteer.levels[0]) {
                const levelObjects = volunteer.levels as Array<{name: string, value: string, order: number}>;
                return levelObjects
                  .sort((a, b) => (a.order || 0) - (b.order || 0)) // 按order排序
                  .map(item => item.value)
                  .filter(Boolean)
                  .join(' > ');
              }
              return '';
            })()
          : '';
        
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>{volunteer.name}</div>
            {volunteer.phone && (
              <div style={{ fontSize: 12, color: '#666' }}>{volunteer.phone}</div>
            )}
            {level && (
              <Tooltip title={level}>
                <div style={{ 
                  fontSize: 12, 
                  color: '#999',
                  maxWidth: 140,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {level}
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: '项目信息',
      key: 'project',
      width: 140,
      align: 'center',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.project?.name || '-'}</div>
          <Tag 
            color={record.project_type === 'survey' ? 'green' : 'orange'}
            style={{ marginTop: 4 }}
          >
            {record.project_type === 'survey' ? '调研项目' : '大使项目'}
          </Tag>
        </div>
      ),
    },
    {
      title: '问卷数量',
      dataIndex: 'survey_count',
      key: 'survey_count',
      width: 100,
      align: 'center',
      render: (count) => `${count || 0} 份`,
    },
    {
      title: '账期',
      dataIndex: 'account_period',
      key: 'account_period',
      width: 240,
      align: 'center',
      render: (period) => period || '-',
    },
    {
      title: '志愿者确认',
      key: 'volunteer_status',
      width: 110,
      align: 'center',
      render: (_, record) => (
        <div>
          <Tag color={getVolunteerStatusColor(record.volunteer_status)}>
            {getVolunteerStatusText(record.volunteer_status)}
          </Tag>
          {record.volunteer_confirmed_at && (
            <div style={{ fontSize: 12, color: '#666' }}>
              {dayjs(record.volunteer_confirmed_at).format('MM-DD HH:mm')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '负责人确认',
      key: 'responsible_status', 
      width: 110,
      align: 'center',
      render: (_, record) => (
        <div>
          <Tag color={getResponsibleStatusColor(record.responsible_status)}>
            {getResponsibleStatusText(record.responsible_status)}
          </Tag>
          {record.responsible_confirmed_at && (
            <div style={{ fontSize: 12, color: '#666' }}>
              {dayjs(record.responsible_confirmed_at).format('MM-DD HH:mm')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '整体状态',
      key: 'overall_status',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Tag color={getOverallStatusColor(record.overall_status)}>
          {getOverallStatusText(record.overall_status)}
        </Tag>
      ),
    },
    {
      title: '确认情况',
      key: 'confirmation_details',
      width: 240,
      align: 'left',
      render: (_, record) => (
        <div>
          <div>原   始: {record.survey_count}份 | ¥{formatAmount(record.original_amount)}</div>
          {record.volunteer_confirmed_count !== undefined && (
            <div style={{color: 'green' }}>
              志愿者确认: {record.volunteer_confirmed_count}份 | ¥{formatAmount(record.volunteer_confirmed_amount)}
            </div>
          )}
          
          {record.responsible_status === 'confirmed' && record.volunteer_confirmed_count !== undefined && (
            <div style={{color: 'blue'}}>
              负责人确认: {record.volunteer_confirmed_count}份 | ¥{formatAmount(record.volunteer_confirmed_amount)}
            </div>
          )}
          
        </div>
      ),
    },
    {
      title: '税后金额',
      dataIndex: 'after_tax_amount',
      key: 'after_tax_amount',
      width: 100,
      align: 'center',
      render: (amount) => `¥${formatAmount(amount)}`,
    },
    {
      title: '税前金额',
      dataIndex: 'before_tax_amount',
      key: 'before_tax_amount',
      width: 100,
      align: 'center',
      render: (amount) => `¥${formatAmount(amount)}`,
    },    
    {
      title: '个人所得税',
      dataIndex: 'tax_amount',
      key: 'tax_amount',
      width: 100,
      align: 'center',
      render: (amount) => `¥${formatAmount(amount)}`,  
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      align: 'center',
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => viewDetail(record)}
          >
            详情
          </Button>
          {(record.overall_status === 'responsible_pending' || record.overall_status === 'admin_pending') && (
            <Button
              type="link"
              size="small"
              icon={<RollbackOutlined />}
              style={{ color: '#fa8c16' }}
              onClick={() => resetToPending(record)}
              loading={resetLoading}
            >
              退回
            </Button>
          )}
          {record.overall_status === 'volunteer_pending' && (
            <Button
              type="link"
              size="small"
              icon={<DeleteOutlined />}
              danger
              onClick={() => deleteLaborDetail(record)}
              loading={deleteLoading}
            >
              删除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record: LaborDetail) => ({
      // 管理员待审核的可以被选中用于审核，志愿者待确认的可以被选中用于删除
      disabled: !(record.overall_status === 'admin_pending' || record.overall_status === 'volunteer_pending')
    })
  };

  const renderActionButtons = () => {
    // 分析选中记录的状态
    const selectedRecords = data.filter(record => selectedRowKeys.includes(record.id));
    const selectedPendingRecords = selectedRecords.filter(record => 
      record.overall_status === 'admin_pending'
    );
    const selectedDeletableRecords = selectedRecords.filter(record => 
      record.overall_status === 'volunteer_pending'
    );

    // 确定批量审核按钮的显示文本和是否显示
    let batchButtonText = '';
    let showBatchButton = false;
    
    if (selectedRowKeys.length === 0) {
      // 没有选择记录时，显示全部符合条件的记录数（从统计数据获取）
      const allPendingCount = confirmationStats?.admin_pending || 0;
      if (allPendingCount > 0) {
        batchButtonText = `批量审核 (全部${allPendingCount}条待审核)`;
        showBatchButton = true;
      }
    } else {
      // 有选择记录时，显示选中的符合条件的记录数
      if (selectedPendingRecords.length > 0) {
        batchButtonText = `批量审核 (${selectedPendingRecords.length}条待审核)`;
        showBatchButton = true;
      }
    }

    // 确定批量删除按钮的显示文本和是否显示
    let batchDeleteButtonText = '';
    let showBatchDeleteButton = false;
    
    if (selectedRowKeys.length === 0) {
      // 没有选择记录时，显示全部符合条件的记录数（从统计数据获取）
      const allVolunteerPendingCount = confirmationStats?.volunteer_pending || 0;
      if (allVolunteerPendingCount > 0) {
        batchDeleteButtonText = `批量删除 (全部${allVolunteerPendingCount}条待确认)`;
        showBatchDeleteButton = true;
      }
    } else {
      // 有选择记录时，显示选中的符合条件的记录数
      if (selectedDeletableRecords.length > 0) {
        batchDeleteButtonText = `批量删除 (${selectedDeletableRecords.length}条待确认)`;
        showBatchDeleteButton = true;
      }
    }

    const settledCount = oaImportStatus?.settledCount || 0;
    const agreementGeneratedCount = oaImportStatus?.agreementGeneratedCount || 0;
    const missingAgreementCount = oaImportStatus?.missingAgreementCount || 0;
    const canGenerateImportCode = oaImportStatus?.canGenerateImportCode === true;
    const importCodeDisabledReason = settledCount === 0
      ? '当前没有已审核待支付记录'
      : missingAgreementCount > 0
        ? `还有 ${missingAgreementCount} 条记录未生成劳务协议`
        : '';

    return (
      <Space style={{ marginBottom: 16 }} wrap>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={generateLaborDetails}
          loading={loading}
        >
          生成劳务明细
        </Button>
        

        {/* 批量审核按钮 */}
        {showBatchButton && (
          <Button 
            icon={<DollarCircleOutlined />}
            onClick={batchSettle}
            type="primary"
          >
            {batchButtonText}
          </Button>
        )}

        {/* 批量删除按钮 */}
        {showBatchDeleteButton && (
          <Button 
            icon={<DeleteOutlined />}
            onClick={batchDeleteLaborDetails}
            danger
            loading={deleteLoading}
          >
            {batchDeleteButtonText}
          </Button>
        )}
        
        <Button 
          icon={<DownloadOutlined />}
          onClick={exportPaymentExcel}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
          type="primary"
        >
          导出已审核待支付表
        </Button>

        <Tooltip title={settledCount === 0 ? '当前没有已审核待支付记录' : `已生成 ${agreementGeneratedCount}/${settledCount} 份`}>
          <Button
            icon={<FileTextOutlined />}
            onClick={generateAllLaborAgreements}
            loading={agreementGenerating}
            disabled={settledCount === 0}
          >
            一键生成劳务协议
          </Button>
        </Tooltip>

        <Tooltip title={importCodeDisabledReason || '生成导入码后可在大地OA中导入'}>
          <Button
            icon={<DownloadOutlined />}
            onClick={generateOaImportCode}
            loading={importCodeGenerating}
            disabled={!canGenerateImportCode}
            style={{ backgroundColor: canGenerateImportCode ? '#13c2c2' : undefined, borderColor: canGenerateImportCode ? '#13c2c2' : undefined }}
            type={canGenerateImportCode ? 'primary' : 'default'}
          >
            生成OA导入码
          </Button>
        </Tooltip>
        
        <Button 
          icon={<PlusOutlined />}
          onClick={() => setImportModalVisible(true)}
          style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
          type="primary"
        >
          导入已支付表
        </Button>
      </Space>
    );
  };

  return (
    <div>
      {/* 美化的面包屑导航 */}
      <div 
        style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          padding: '20px 24px',
          marginBottom: '20px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* 背景装饰 */}
        <div 
          style={{
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '200px',
            height: '200px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            zIndex: 1
          }}
        />
        <div 
          style={{
            position: 'absolute',
            bottom: '-30%',
            left: '-5%',
            width: '150px',
            height: '150px',
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '50%',
            zIndex: 1
          }}
        />
        
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          position: 'relative',
          zIndex: 2
        }}>
          {/* 美化的面包屑 */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Breadcrumb 
              separator={
                <span style={{ 
                  color: 'rgba(255, 255, 255, 0.6)', 
                  margin: '0 8px',
                  lineHeight: '22px',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}>
                  /
                </span>
              }
              style={{ 
                color: 'white',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Breadcrumb.Item>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '14px',
                  lineHeight: '22px',
                  height: '22px'
                }}>
                  <HomeOutlined style={{ fontSize: '16px', marginRight: '4px' }} />
                  <span>首页</span>
                </div>
              </Breadcrumb.Item>
              
              <Breadcrumb.Item>
                <Button 
                  type="text"
                  onClick={() => navigate('/project-labor-detail-index')}
                  style={{ 
                    color: 'rgba(255, 255, 255, 0.9)',
                    padding: '0 8px',
                    height: '22px',
                    lineHeight: '22px',
                    borderRadius: '6px',
                    transition: 'all 0.3s ease',
                    fontSize: '14px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    minHeight: '22px'
                  }}
                  onMouseEnter={(e) => {
                    const target = e.target as HTMLElement;
                    target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                    target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    const target = e.target as HTMLElement;
                    target.style.backgroundColor = 'transparent';
                    target.style.color = 'rgba(255, 255, 255, 0.9)';
                  }}
                >
                  项目劳务管理
                </Button>
              </Breadcrumb.Item>
              
              <Breadcrumb.Item>
                <div style={{ 
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: '22px',
                  height: '22px'
                }}>
                  <FileTextOutlined style={{ marginRight: '6px', fontSize: '16px' }} />
                  {currentProject?.name || projectName || '项目劳务明细'}
                </div>
              </Breadcrumb.Item>
            </Breadcrumb>
          </div>
          
          {/* 美化的返回按钮 */}
          <Button 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/project-labor-detail-index')}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: 'white',
              borderRadius: '8px',
              fontWeight: '500',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              const target = e.target as HTMLElement;
              target.style.background = 'rgba(255, 255, 255, 0.3)';
              target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
              target.style.transform = 'translateY(-1px)';
              target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
            }}
            onMouseLeave={(e) => {
              const target = e.target as HTMLElement;
              target.style.background = 'rgba(255, 255, 255, 0.2)';
              target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              target.style.transform = 'translateY(0)';
              target.style.boxShadow = 'none';
            }}
          >
            返回项目列表
          </Button>
        </div>
      </div>

      {/* 项目信息卡片 */}
      {(currentProject || projectName) && (
        <Card 
          title="当前项目信息" 
          style={{ marginBottom: '16px' }}
          size="small"
        >
          <Descriptions column={4} size="small">
            <Descriptions.Item label="项目名称">
              {currentProject?.name || projectName}
            </Descriptions.Item>
            <Descriptions.Item label="项目类型">
              <Tag color={currentProject?.project_type === 'survey' ? 'blue' : 'orange'}>
                {currentProject?.project_type === 'survey' ? '调研项目' : 
                 currentProject?.project_type === 'ambassador' ? '大使项目' :
                 projectType === 'survey' ? '调研项目' :
                 projectType === 'ambassador' ? '大使项目' : '未知'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="项目ID">
              {projectId}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      <Card title="劳务明细管理">
        {/* 确认状态统计卡片 */}
        {confirmationStats && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={4}>
              <Card>
                <Statistic 
                  title="志愿者待确认" 
                  value={confirmationStats.volunteer_pending} 
                  valueStyle={{ color: '#faad14' }}
                  suffix="条"
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic 
                  title="负责人待确认" 
                  value={confirmationStats.responsible_pending} 
                  valueStyle={{ color: '#fa8c16' }}
                  suffix="条"
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic 
                  title="管理员待审核" 
                  value={confirmationStats.admin_pending} 
                  valueStyle={{ color: '#1890ff' }}
                  suffix="条"
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic 
                  title="已审核待支付" 
                  value={confirmationStats.admin_settled} 
                  valueStyle={{ color: '#52c41a' }}
                  suffix="条"
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic 
                  title="已支付" 
                  value={confirmationStats.paid} 
                  valueStyle={{ color: '#722ed1' }}
                  suffix="条"
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* 金额统计卡片 */}
        {confirmationStats && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Card>
                <Statistic 
                  title="总金额" 
                  value={confirmationStats.total_amount} 
                  prefix="¥"
                  precision={2}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Statistic 
                  title="已确认金额" 
                  value={confirmationStats.confirmed_amount} 
                  prefix="¥"
                  precision={2}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* 搜索表单 */}
        <Form 
          form={searchForm} 
          layout="inline" 
          style={{ marginBottom: 16 }}
          onFinish={handleSearch}
        >
          <Form.Item name="doctor_id">
            <Select
              placeholder="搜索医生"
              style={{ width: 150 }}
              allowClear
              showSearch
              filterOption={false}
              onSearch={handleDoctorSearch}
              loading={doctorSearchLoading}
              onFocus={() => {
                if (doctors.length === 0) searchDoctors('');
              }}
            >
              {doctors.map(doctor => (
                <Option key={doctor.id} value={doctor.id}>
                  {doctor.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          

          
          <Form.Item name="overall_status">
            <Select
              placeholder="整体状态"
              style={{ width: 140 }}
              allowClear
            >
              <Option value="volunteer_pending">志愿者待确认</Option>
              <Option value="responsible_pending">负责人待确认</Option>
              <Option value="admin_pending">管理员待审核</Option>
              <Option value="admin_settled">已审核待支付</Option>
              <Option value="paid">已支付</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
          </Form.Item>

          
          <Form.Item name="dateRange">
            <RangePicker />
          </Form.Item>
          
          
          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit"
                icon={<SearchOutlined />}
              >
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 操作按钮 */}
        {renderActionButtons()}

        {/* 数据表格 */}
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, size) => {
              fetchData(page, size, searchForm.getFieldsValue());
            }
          }}
          scroll={{ x: 1500 }}
        />
      </Card>



      {/* 详情弹窗 */}
      <Modal
        title="劳务明细详情"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedDetail(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setDetailModalVisible(false);
            setSelectedDetail(null);
          }}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {selectedDetail && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="劳务ID">{selectedDetail.id}</Descriptions.Item>
            <Descriptions.Item label="支付状态">
              <Tag color={getOverallStatusColor(selectedDetail.overall_status)}>
                {getOverallStatusText(selectedDetail.overall_status)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="劳务协议状态">
              {selectedDetail.agreementDocument?.status === 'generated' ? (
                <Tag color="success">已生成</Tag>
              ) : selectedDetail.agreementDocument?.status === 'failed' ? (
                <Tag color="error">生成失败</Tag>
              ) : (
                <Tag>未生成</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="劳务协议">
              {selectedDetail.agreementDocument?.file_url ? (
                <a href={selectedDetail.agreementDocument.file_url} target="_blank" rel="noreferrer">
                  查看协议
                </a>
              ) : selectedDetail.agreementDocument?.error_message ? (
                <Tooltip title={selectedDetail.agreementDocument.error_message}>
                  <span style={{ color: '#ff4d4f' }}>查看失败原因</span>
                </Tooltip>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="医生姓名">{selectedDetail.doctor?.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="医生电话">{selectedDetail.doctor?.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="医院名称">{selectedDetail.doctor?.hospitalName || '-'}</Descriptions.Item>
            <Descriptions.Item label="科室名称">{selectedDetail.doctor?.departmentName || '-'}</Descriptions.Item>
            <Descriptions.Item label="项目名称">{selectedDetail.project?.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="问卷数量">{selectedDetail.survey_count} 份</Descriptions.Item>
            <Descriptions.Item label="账期">{selectedDetail.account_period || '-'}</Descriptions.Item>
            <Descriptions.Item label="税后金额">¥{formatAmount(selectedDetail.after_tax_amount)}</Descriptions.Item>
            <Descriptions.Item label="税前金额">¥{formatAmount(selectedDetail.before_tax_amount)}</Descriptions.Item>
            <Descriptions.Item label="个人所得税">¥{formatAmount(selectedDetail.tax_amount)}</Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {selectedDetail.createdAt 
                ? dayjs(selectedDetail.createdAt).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Descriptions.Item>
            {['admin_settled', 'paid'].includes(selectedDetail.overall_status) && (
              <>
                <Descriptions.Item label="审核时间">
                  {selectedDetail.settled_at 
                    ? dayjs(selectedDetail.settled_at).format('YYYY-MM-DD HH:mm:ss')
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="审核人">
                  {selectedDetail.settler?.name || '-'}
                </Descriptions.Item>
              </>
            )}
            {selectedDetail.overall_status === 'paid' && (
              <>
                <Descriptions.Item label="支付时间">
                  {selectedDetail.paid_at 
                    ? dayjs(selectedDetail.paid_at).format('YYYY-MM-DD HH:mm:ss')
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="支付人">
                  {selectedDetail.payer?.name || '-'}
                </Descriptions.Item>
              </>
            )}
            {selectedDetail.remark && (
              <Descriptions.Item label="备注" span={2}>
                {selectedDetail.remark}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* OA导入码弹窗 */}
      <Modal
        title="OA导入码"
        open={importCodeModalVisible}
        onCancel={() => setImportCodeModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setImportCodeModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={560}
      >
        {generatedImportCode && (
          <div>
            <p style={{ color: '#666' }}>
              请在大地OA的“导入问卷项目”中填写以下导入码。
            </p>
            <div style={{ textAlign: 'center', margin: '24px 0' }}>
              <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: 4, color: '#13c2c2' }}>
                {generatedImportCode.code}
              </div>
              <Button
                style={{ marginTop: 12 }}
                onClick={() => {
                  navigator.clipboard?.writeText(generatedImportCode.code);
                  message.success('导入码已复制');
                }}
              >
                复制导入码
              </Button>
            </div>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="记录数">{generatedImportCode.detailCount} 条</Descriptions.Item>
              <Descriptions.Item label="税后总金额">¥{formatAmount(generatedImportCode.totalAmount)}</Descriptions.Item>
              <Descriptions.Item label="有效期至">
                {generatedImportCode.expires_at
                  ? dayjs(generatedImportCode.expires_at).format('YYYY-MM-DD HH:mm:ss')
                  : '-'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* 导入支付结果弹窗 */}
      <Modal
        title="导入支付结果"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ padding: '20px 0' }}>
          <p style={{ marginBottom: 16, color: '#666' }}>
            请上传包含"支付结果"列的Excel文件，支付结果列应填写"成功"表示支付完成。
          </p>
          <Upload.Dragger
            name="file"
            accept=".xlsx,.xls"
            beforeUpload={(file) => {
              handleImportPaymentResult(file);
              return false; // 阻止自动上传
            }}
            showUploadList={false}
            disabled={uploading}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">
              {uploading ? '正在上传...' : '点击或拖拽文件到此区域上传'}
            </p>
            <p className="ant-upload-hint">
              支持 .xlsx 和 .xls 格式的Excel文件
            </p>
          </Upload.Dragger>
          
          <div style={{ marginTop: 16, fontSize: 12, color: '#999' }}>
            <p><strong>注意事项：</strong></p>
            <ul style={{ paddingLeft: 20 }}>
              <li>文件格式：Excel (.xlsx 或 .xls)</li>
              <li>必须包含"支付结果"列</li>
              <li>支付成功的记录请在"支付结果"列填写"成功"</li>
              <li>其他值将被忽略</li>
              <li>建议先导出支付确认表模板，填写后再导入</li>
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LaborDetailManagement; 
